import { Avatar, Button, Spinner } from '@billda/design-system';
import { SearchIcon } from '@billda/design-system';
import { GangSearchUser } from '../../hooks/useGangMemberSelection';
import { ReactNode } from 'react';

interface GangMemberSearchResultsProps {
  searchQuery: string;
  users: GangSearchUser[];
  isLoading: boolean;
  canSelectMore: boolean;
  onSelectUser: (user: GangSearchUser) => void;
  onLoadMore: () => void;
  canLoadMore: boolean;
}

export const GangMemberSearchResults = ({
  searchQuery,
  users,
  isLoading,
  canSelectMore,
  onSelectUser,
  onLoadMore,
  canLoadMore,
}: GangMemberSearchResultsProps) => {
  const showSearchPrompt = searchQuery.length === 0;
  const needsMoreCharacters = searchQuery.length > 0 && searchQuery.length < 3;
  const noResults = searchQuery.length >= 3 && users.length === 0 && !isLoading;
  const showInitialLoading =
    isLoading && searchQuery.length >= 3 && users.length === 0;

  return (
    <div className="max-h-[400px] overflow-y-auto rounded-lg border border-border-default bg-surface-canvas p-2">
      {showSearchPrompt && (
        <EmptyState
          icon={
            <SearchIcon aria-hidden="true" className="size-12 opacity-50" />
          }
          message="Please search for users above to see available options."
        />
      )}

      {needsMoreCharacters && (
        <EmptyState
          icon={
            <SearchIcon aria-hidden="true" className="size-12 opacity-50" />
          }
          message="Please enter at least 3 characters to search for users."
          messageClassName="text-content-info"
        />
      )}

      {noResults && (
        <EmptyState
          icon={
            <SearchIcon aria-hidden="true" className="size-12 opacity-50" />
          }
          message="No users found matching your search. Try a different term."
          messageClassName="text-content-warning"
        />
      )}

      {showInitialLoading && (
        <EmptyState
          icon={<Spinner data-testid="search-loading-indicator" size={48} />}
          message="Searching for users..."
        />
      )}

      {users.length > 0 &&
        users.map(user => (
          <div
            key={user.id}
            className={`mb-2 flex items-center justify-between rounded-xl border border-border-default bg-surface-default p-4 ${
              canSelectMore
                ? 'cursor-pointer opacity-100 hover:bg-surface-raised'
                : 'cursor-not-allowed opacity-50'
            }`}
            data-testid={`available-${user.id}`}
            onClick={() => {
              if (canSelectMore) {
                onSelectUser(user);
              }
            }}
          >
            <div className="flex items-center gap-4">
              <Avatar aria-label={user.fullName} className="size-8">
                {user.fullName.charAt(0).toUpperCase()}
              </Avatar>
              <div>
                <p className="type-body-lg font-medium">{user.fullName}</p>
                <p className="type-body-md text-content-secondary">
                  {user.email}
                </p>
              </div>
            </div>

            <Button
              variant="outline"
              size="sm"
              disabled={!canSelectMore}
              onClick={event => {
                event.stopPropagation();
                if (canSelectMore) {
                  onSelectUser(user);
                }
              }}
              data-testid={`select-button-${user.id}`}
            >
              Select
            </Button>
          </div>
        ))}

      {users.length > 0 && canLoadMore && (
        <div className="mb-2 mt-4 flex justify-center">
          <Button
            variant="outline"
            onClick={onLoadMore}
            disabled={isLoading}
            fullWidth
            className="max-w-[200px]"
            data-testid="load-more-button"
          >
            Load More
          </Button>
        </div>
      )}
    </div>
  );
};

interface EmptyStateProps {
  icon: ReactNode;
  message: string;
  messageClassName?: string;
}

const EmptyState = ({ icon, message, messageClassName }: EmptyStateProps) => (
  <div
    className={`py-8 text-center ${messageClassName ?? 'text-content-secondary'}`}
  >
    <div className="mb-1 flex justify-center">{icon}</div>
    <p className="type-body-md">{message}</p>
  </div>
);
